import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Send, X, PenLine } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ size = 18 }) => (
  <div className="flex gap-1" aria-label="5 out of 5 stars" role="img">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} className="text-[#C58A47]" fill="#C58A47" strokeWidth={1} />
    ))}
  </div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", business: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => setReviews(data.reviews))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, rating: 5 });
      setSent(true);
      toast.success("Thanks! Your review will appear once we've had a quick look.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const openModal = () => {
    setSent(false);
    setOpen(true);
  };

  const inputCls =
    "w-full bg-white border border-[#E2DACB] px-4 py-3 text-sm text-[#233D35] placeholder-[#77786F]/60 focus:border-[#C58A47] focus:outline-none transition-colors";

  return (
    <section className="bg-[#EBE5D8] py-12 sm:py-16 lg:py-20" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Kind Words</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#233D35] mt-3 leading-[0.95]">
            Good words from good people.
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.1}>
              <figure className="bg-white border border-[#E2DACB] p-6 sm:p-8 h-full shadow-[0_4px_20px_rgba(35,61,53,0.06)]" data-testid={`review-card-${i}`}>
                <Stars />
                <blockquote className="mt-4 text-[#233D35]/90 text-base leading-relaxed">
                  &ldquo;{r.message}&rdquo;
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-[#E2DACB]">
                  <span className="font-head font-bold uppercase tracking-tight text-lg text-[#233D35] block">{r.name}</span>
                  {r.business && <span className="text-sm text-[#77786F]">{r.business}</span>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 text-center">
          <p className="text-[#77786F] text-base">Worked with us? We'd love to hear about it.</p>
          <button
            onClick={openModal}
            data-testid="review-open-button"
            className="group mt-4 inline-flex items-center gap-2 bg-[#233D35] hover:bg-[#1A2621] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] px-7 py-4 transition-colors duration-200"
          >
            <PenLine size={17} className="text-[#C58A47]" />
            Leave a Review
          </button>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="review-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] bg-[#1A2621]/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setOpen(false)}
            data-testid="review-modal-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              role="dialog"
              aria-modal="true"
              aria-label="Leave a review"
              className="bg-white border border-[#E2DACB] w-full max-w-lg p-6 sm:p-8 relative shadow-[0_20px_60px_rgba(26,38,33,0.35)]"
              onClick={(e) => e.stopPropagation()}
              data-testid="review-modal"
            >
              <button
                onClick={() => setOpen(false)}
                aria-label="Close review form"
                data-testid="review-modal-close"
                className="absolute top-4 right-4 w-10 h-10 inline-flex items-center justify-center text-[#77786F] hover:text-[#233D35] transition-colors"
              >
                <X size={22} />
              </button>

              {sent ? (
                <div className="py-10 text-center" data-testid="review-success">
                  <div className="flex justify-center"><Stars size={22} /></div>
                  <span className="font-head font-bold uppercase tracking-tight text-2xl text-[#233D35] block mt-4">Thank you.</span>
                  <p className="mt-3 text-sm text-[#77786F] leading-relaxed max-w-sm mx-auto">
                    Your review is in and will appear on the homepage once we've had a quick look.
                  </p>
                  <button
                    onClick={() => setOpen(false)}
                    data-testid="review-success-close"
                    className="mt-6 inline-flex items-center gap-2 border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="review-form">
                  <span className="font-head font-bold uppercase tracking-[0.2em] text-xs text-[#C58A47]">Leave a Review</span>
                  <div className="mt-3"><Stars size={22} /></div>
                  <p className="mt-3 text-xs text-[#77786F] leading-relaxed">
                    Only five-star reviews can be sent through the website — if we earned fewer, email goodworkweb1@gmail.com and we'll make it right.
                  </p>
                  <div className="mt-5 space-y-3">
                    <div>
                      <label htmlFor="r-name" className="block text-sm font-semibold text-[#233D35] mb-1.5">Name *</label>
                      <input id="r-name" required data-testid="review-name" className={inputCls} value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
                    </div>
                    <div>
                      <label htmlFor="r-business" className="block text-sm font-semibold text-[#233D35] mb-1.5">Business</label>
                      <input id="r-business" data-testid="review-business" className={inputCls} value={form.business}
                        onChange={(e) => setForm({ ...form, business: e.target.value })} placeholder="Your business (optional)" />
                    </div>
                    <div>
                      <label htmlFor="r-email" className="block text-sm font-semibold text-[#233D35] mb-1.5">Email *</label>
                      <input id="r-email" type="email" required data-testid="review-email" className={inputCls} value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                      <p className="mt-1 text-xs text-[#77786F]">Only we see this — it's never published — and it lets us thank you.</p>
                    </div>
                    <div>
                      <label htmlFor="r-message" className="block text-sm font-semibold text-[#233D35] mb-1.5">Your review *</label>
                      <textarea id="r-message" required rows={3} data-testid="review-message" className={inputCls} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How was your experience?" />
                    </div>
                  </div>
                  <button type="submit" disabled={sending} data-testid="review-submit"
                    className="group mt-5 inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] disabled:opacity-60 text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3 transition-colors duration-200">
                    {sending ? "Sending…" : "Send Review"}
                    <Send size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Reviews;
